import * as THREE from 'three';
import {
  bonusCardSeedToUint32,
  getBonusCardVariant,
  getBonusCardVariantForDenomination,
  pickBonusCardVariant,
  type BonusCardDenomination,
  type BonusCardSeed,
  type BonusCardVariant,
  type BonusCardVariantId,
} from './catalog';
import {
  BONUS_CARD_DIMENSIONS,
  createBonusCardBodyGeometry,
  createBonusCardFaceGeometry,
  createBonusCardSparkGeometry,
  createBonusCardStudGeometry,
  createBonusCardTrimGeometry,
  getBonusCardStudPositions,
  type BonusCardDimensions,
} from './geometry';
import { createBonusCardTexturePair, type BonusCardTexturePair } from './textures';

export type BonusCardCreateOptions = Readonly<{
  variantId?: BonusCardVariantId;
  denomination?: BonusCardDenomination;
  seed?: BonusCardSeed;
  instanceId?: string;
  castShadow?: boolean;
  receiveShadow?: boolean;
}>;

export type BonusCardCollisionSpec = Readonly<{
  shape: 'cuboid';
  /** Full collider dimensions on the card's local X, Y, and Z axes. */
  size: readonly [x: number, y: number, z: number];
  /** Ready for RAPIER.ColliderDesc.cuboid(x, y, z). */
  halfExtents: readonly [x: number, y: number, z: number];
  localCenter: readonly [x: number, y: number, z: number];
  friction: number;
  restitution: number;
  density: number;
  linearDamping: number;
  angularDamping: number;
  ccd: boolean;
  canSleep: boolean;
}>;

export type BonusCardRuntimeMetadata = Readonly<{
  schemaVersion: 1;
  assetType: 'bonus-card';
  instanceId: string;
  variantId: BonusCardVariantId;
  variantIndex: number;
  denomination: BonusCardDenomination;
  denominationLabel: string;
  displayName: string;
  themeName: string;
  rewardUnit: 'credits';
  selectionSeed: number;
  tags: readonly string[];
  orientation: Readonly<{
    widthAxis: 'local-x';
    heightAxis: 'local-y';
    thicknessAxis: 'local-z';
    frontNormal: readonly [0, 0, 1];
    backNormal: readonly [0, 0, -1];
  }>;
  collision: BonusCardCollisionSpec;
}>;

type BonusCardPart = 'body' | 'front-art' | 'back-art' | 'front-trim' | 'back-trim' | 'foil-stud' | 'foil-spark';

type VariantResources = Readonly<{
  textures: BonusCardTexturePair;
  bodyMaterial: THREE.MeshPhysicalMaterial;
  frontMaterial: THREE.MeshPhysicalMaterial;
  backMaterial: THREE.MeshPhysicalMaterial;
  foilMaterial: THREE.MeshPhysicalMaterial;
}>;

export class BonusCardObject {
  private disposed = false;

  constructor(
    readonly root: THREE.Group,
    readonly metadata: BonusCardRuntimeMetadata,
    readonly frontFace: THREE.Mesh<THREE.ShapeGeometry, THREE.MeshPhysicalMaterial>,
    readonly backFace: THREE.Mesh<THREE.ShapeGeometry, THREE.MeshPhysicalMaterial>,
    private readonly onDispose: (object: BonusCardObject) => void,
  ) {}

  get collision(): BonusCardCollisionSpec {
    return this.metadata.collision;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.root.removeFromParent();
    this.root.clear();
    this.onDispose(this);
  }
}

export class BonusCardAssetLibrary {
  readonly dimensions: BonusCardDimensions;

  private readonly bodyGeometry: THREE.ExtrudeGeometry;
  private readonly faceGeometry: THREE.ShapeGeometry;
  private readonly trimGeometry: THREE.ExtrudeGeometry;
  private readonly studGeometry: THREE.SphereGeometry;
  private readonly sparkGeometry: THREE.ExtrudeGeometry;
  private readonly variantResources = new Map<BonusCardVariantId, VariantResources>();
  private readonly instances = new Set<BonusCardObject>();
  private disposed = false;

  constructor(dimensions: BonusCardDimensions = BONUS_CARD_DIMENSIONS) {
    this.dimensions = Object.freeze({ ...dimensions });
    this.bodyGeometry = createBonusCardBodyGeometry(this.dimensions);
    this.faceGeometry = createBonusCardFaceGeometry(this.dimensions);
    this.trimGeometry = createBonusCardTrimGeometry(this.dimensions);
    this.studGeometry = createBonusCardStudGeometry(this.dimensions);
    this.sparkGeometry = createBonusCardSparkGeometry(this.dimensions);

    this.bodyGeometry.name = 'BonusCardGeometry:Body';
    this.faceGeometry.name = 'BonusCardGeometry:Face';
    this.trimGeometry.name = 'BonusCardGeometry:Trim';
    this.studGeometry.name = 'BonusCardGeometry:Stud';
    this.sparkGeometry.name = 'BonusCardGeometry:Spark';
  }

  create(options: BonusCardCreateOptions = {}): BonusCardObject {
    this.assertAvailable();
    const variant = resolveVariant(options);
    const seed = bonusCardSeedToUint32(options.seed ?? options.instanceId ?? variant.id);
    const instanceId = options.instanceId ?? createDeterministicInstanceId(variant.id, seed);
    const collision = createBonusCardCollisionSpec(this.dimensions);
    const metadata = createRuntimeMetadata(variant, instanceId, seed, collision);
    const resources = this.getVariantResources(variant);
    const root = this.createVisual(metadata, resources, options);

    let object: BonusCardObject;
    const frontFace = root.getObjectByName(`${root.name}:FrontArtwork`) as BonusCardObject['frontFace'];
    const backFace = root.getObjectByName(`${root.name}:BackArtwork`) as BonusCardObject['backFace'];
    object = new BonusCardObject(root, metadata, frontFace, backFace, (released) => {
      this.instances.delete(released);
    });
    this.instances.add(object);
    return object;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const instance of [...this.instances]) instance.dispose();
    this.bodyGeometry.dispose();
    this.faceGeometry.dispose();
    this.trimGeometry.dispose();
    this.studGeometry.dispose();
    this.sparkGeometry.dispose();

    for (const resources of this.variantResources.values()) {
      resources.textures.front.dispose();
      resources.textures.back.dispose();
      resources.bodyMaterial.dispose();
      resources.frontMaterial.dispose();
      resources.backMaterial.dispose();
      resources.foilMaterial.dispose();
    }
    this.variantResources.clear();
  }

  private createVisual(
    metadata: BonusCardRuntimeMetadata,
    resources: VariantResources,
    options: BonusCardCreateOptions,
  ): THREE.Group {
    const root = new THREE.Group();
    root.name = `BonusCard:${metadata.instanceId}`;
    root.userData = {
      assetType: 'bonus-card',
      bonusCard: metadata,
      runtime: metadata,
    };

    const castShadow = options.castShadow ?? true;
    const receiveShadow = options.receiveShadow ?? true;
    const faceZ = this.dimensions.bodyThickness * 0.5 + 0.001;
    const trimZ = faceZ + this.dimensions.accentRise * 0.42;

    const body = new THREE.Mesh(this.bodyGeometry, resources.bodyMaterial);
    configurePart(body, root, 'body', castShadow, receiveShadow);
    root.add(body);

    const frontFace = new THREE.Mesh(this.faceGeometry, resources.frontMaterial);
    frontFace.position.z = faceZ;
    configurePart(frontFace, root, 'front-art', castShadow, receiveShadow, 'FrontArtwork');
    root.add(frontFace);

    const backFace = new THREE.Mesh(this.faceGeometry, resources.backMaterial);
    backFace.position.z = -faceZ;
    backFace.rotation.y = Math.PI;
    configurePart(backFace, root, 'back-art', castShadow, receiveShadow, 'BackArtwork');
    root.add(backFace);

    const frontTrim = new THREE.Mesh(this.trimGeometry, resources.foilMaterial);
    frontTrim.position.z = trimZ;
    configurePart(frontTrim, root, 'front-trim', castShadow, receiveShadow);
    root.add(frontTrim);

    const backTrim = new THREE.Mesh(this.trimGeometry, resources.foilMaterial);
    backTrim.position.z = -trimZ;
    backTrim.rotation.y = Math.PI;
    configurePart(backTrim, root, 'back-trim', castShadow, receiveShadow);
    root.add(backTrim);

    const studZ = faceZ + this.dimensions.accentRise * 0.72;
    getBonusCardStudPositions(this.dimensions).forEach(([x, y], index) => {
      const frontStud = new THREE.Mesh(this.studGeometry, resources.foilMaterial);
      frontStud.position.set(x, y, studZ);
      frontStud.scale.z = 0.48;
      configurePart(frontStud, root, 'foil-stud', castShadow, receiveShadow, `FrontStud${index + 1}`);
      root.add(frontStud);

      const backStud = new THREE.Mesh(this.studGeometry, resources.foilMaterial);
      backStud.position.set(x, y, -studZ);
      backStud.scale.z = 0.48;
      configurePart(backStud, root, 'foil-stud', castShadow, receiveShadow, `BackStud${index + 1}`);
      root.add(backStud);
    });

    const sparkX = -this.dimensions.width * 0.34;
    const sparkY = this.dimensions.height * 0.31;
    const frontSpark = new THREE.Mesh(this.sparkGeometry, resources.foilMaterial);
    frontSpark.position.set(sparkX, sparkY, trimZ + this.dimensions.accentRise * 0.25);
    configurePart(frontSpark, root, 'foil-spark', castShadow, receiveShadow, 'FrontSpark');
    root.add(frontSpark);

    const backSpark = new THREE.Mesh(this.sparkGeometry, resources.foilMaterial);
    backSpark.position.set(-sparkX, -sparkY, -(trimZ + this.dimensions.accentRise * 0.25));
    backSpark.rotation.y = Math.PI;
    configurePart(backSpark, root, 'foil-spark', castShadow, receiveShadow, 'BackSpark');
    root.add(backSpark);

    return root;
  }

  private getVariantResources(variant: BonusCardVariant): VariantResources {
    const existing = this.variantResources.get(variant.id);
    if (existing) return existing;

    const textures = createBonusCardTexturePair(variant);
    const bodyMaterial = new THREE.MeshPhysicalMaterial({
      name: `BonusCardMaterial:Body:${variant.id}`,
      color: variant.palette.edge,
      roughness: 0.3,
      metalness: 0.12,
      clearcoat: 0.72,
      clearcoatRoughness: 0.2,
      sheen: 0.22,
      sheenColor: new THREE.Color(variant.palette.bright),
    });
    const frontMaterial = createFaceMaterial(variant, textures.front, 'Front');
    const backMaterial = createFaceMaterial(variant, textures.back, 'Back');
    const foilMaterial = new THREE.MeshPhysicalMaterial({
      name: `BonusCardMaterial:Foil:${variant.id}`,
      color: variant.palette.foil,
      emissive: variant.palette.foil,
      emissiveIntensity: 0.035,
      metalness: 0.9,
      roughness: 0.17,
      clearcoat: 1,
      clearcoatRoughness: 0.08,
      iridescence: 0.24,
      iridescenceIOR: 1.45,
      iridescenceThicknessRange: [120, 340],
    });

    const resources = Object.freeze({ textures, bodyMaterial, frontMaterial, backMaterial, foilMaterial });
    this.variantResources.set(variant.id, resources);
    return resources;
  }

  private assertAvailable(): void {
    if (this.disposed) throw new Error('This BonusCardAssetLibrary has been disposed.');
  }
}

let sharedLibrary: BonusCardAssetLibrary | undefined;

export function createBonusCard(options: BonusCardCreateOptions = {}): BonusCardObject {
  sharedLibrary ??= new BonusCardAssetLibrary();
  return sharedLibrary.create(options);
}

export function disposeSharedBonusCardAssets(): void {
  sharedLibrary?.dispose();
  sharedLibrary = undefined;
}

export function createBonusCardCollisionSpec(
  dimensions: BonusCardDimensions = BONUS_CARD_DIMENSIONS,
): BonusCardCollisionSpec {
  const padding = 0.008;
  const size = Object.freeze([
    dimensions.width + padding * 2,
    dimensions.height + padding * 2,
    dimensions.bodyThickness + dimensions.accentRise * 2 + padding * 2,
  ] as const);
  const halfExtents = Object.freeze([size[0] * 0.5, size[1] * 0.5, size[2] * 0.5] as const);
  return Object.freeze({
    shape: 'cuboid',
    size,
    halfExtents,
    localCenter: Object.freeze([0, 0, 0] as const),
    friction: 0.32,
    restitution: 0.08,
    density: 0.44,
    linearDamping: 0.09,
    angularDamping: 0.15,
    ccd: false,
    canSleep: true,
  });
}

function resolveVariant(options: BonusCardCreateOptions): BonusCardVariant {
  if (options.variantId) {
    const variant = getBonusCardVariant(options.variantId);
    if (options.denomination !== undefined && variant.denomination !== options.denomination) {
      throw new Error(`Bonus card variant ${variant.id} does not match denomination ${options.denomination}.`);
    }
    return variant;
  }
  if (options.denomination !== undefined) {
    return getBonusCardVariantForDenomination(options.denomination);
  }
  return pickBonusCardVariant(options.seed ?? 'bonus-card-default');
}

function createRuntimeMetadata(
  variant: BonusCardVariant,
  instanceId: string,
  seed: number,
  collision: BonusCardCollisionSpec,
): BonusCardRuntimeMetadata {
  const variantIndex = ['sun-pop-50', 'star-pulse-100', 'tidal-neon-200', 'emerald-facet-500', 'cosmic-orbit-1000'].indexOf(
    variant.id,
  );
  return Object.freeze({
    schemaVersion: 1,
    assetType: 'bonus-card',
    instanceId,
    variantId: variant.id,
    variantIndex,
    denomination: variant.denomination,
    denominationLabel: variant.denominationLabel,
    displayName: variant.displayName,
    themeName: variant.themeName,
    rewardUnit: 'credits',
    selectionSeed: seed,
    tags: Object.freeze(['prize', 'collectible', 'bonus-card', variant.themeName.toLowerCase().replaceAll(' ', '-')]),
    orientation: Object.freeze({
      widthAxis: 'local-x',
      heightAxis: 'local-y',
      thicknessAxis: 'local-z',
      frontNormal: Object.freeze([0, 0, 1] as const),
      backNormal: Object.freeze([0, 0, -1] as const),
    }),
    collision,
  });
}

function createDeterministicInstanceId(variantId: BonusCardVariantId, seed: number): string {
  const fingerprint = bonusCardSeedToUint32(`${variantId}:${seed}`).toString(36).padStart(7, '0');
  return `${variantId}-${fingerprint}`;
}

function createFaceMaterial(
  variant: BonusCardVariant,
  texture: THREE.CanvasTexture,
  sideName: 'Front' | 'Back',
): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    name: `BonusCardMaterial:${sideName}:${variant.id}`,
    map: texture,
    color: '#ffffff',
    roughness: 0.26,
    metalness: 0.05,
    clearcoat: 0.78,
    clearcoatRoughness: 0.16,
    sheen: 0.16,
    sheenColor: new THREE.Color(variant.palette.paper),
    side: THREE.FrontSide,
  });
}

function configurePart(
  mesh: THREE.Mesh,
  root: THREE.Group,
  part: BonusCardPart,
  castShadow: boolean,
  receiveShadow: boolean,
  suffix?: string,
): void {
  const partName = suffix ?? partToName(part);
  mesh.name = `${root.name}:${partName}`;
  mesh.castShadow = castShadow;
  mesh.receiveShadow = receiveShadow;
  mesh.userData = { assetType: 'bonus-card-part', part, owner: root.name };
}

function partToName(part: BonusCardPart): string {
  return part
    .split('-')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join('');
}
